import { useState } from 'react'
import { Plus } from 'lucide-react'
import { useItemsBoardStore } from '@/stores/items-board-store'
import type { ItemPriority } from '@/types/dashboard'

const priorityOptions = [
  { value: 'money' as ItemPriority, label: '1 - Money', color: 'bg-[#63d706]/20 text-[#63d706] border-[#63d706]/40 hover:bg-[#63d706]/30' },
  { value: 'growth' as ItemPriority, label: '2 - Growth', color: 'bg-[#3b82f6]/20 text-[#3b82f6] border-[#3b82f6]/40 hover:bg-[#3b82f6]/30' },
  { value: 'critical' as ItemPriority, label: '3 - Critical', color: 'bg-[#d70606]/20 text-[#d70606] border-[#d70606]/40 hover:bg-[#d70606]/30' },
  { value: 'backlog' as ItemPriority, label: 'Backlog', color: 'bg-white/5 text-white/60 border-white/10 hover:bg-white/10' },
]

export function AddItemButton() {
  const [isAdding, setIsAdding] = useState(false)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [priorities, setPriorities] = useState<ItemPriority[]>(['backlog'])
  const { addItem } = useItemsBoardStore()

  const togglePriority = (priority: ItemPriority) => {
    setPriorities((prev) => {
      if (prev.includes(priority)) {
        // If it's the last one, don't allow removal
        if (prev.length === 1) return prev
        return prev.filter((p) => p !== priority)
      } else {
        return [...prev, priority]
      }
    })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (title.trim() && priorities.length > 0) {
      const priorityValue = priorities.length === 1 ? priorities[0] : priorities

      // Determine status based on priority
      const itemStatus = priorities.includes('backlog') ? 'future' : 'current'

      addItem(title.trim(), priorityValue, itemStatus, description.trim() || undefined)
      setTitle('')
      setDescription('')
      setPriorities(['backlog'])
      setIsAdding(false)
    }
  }

  if (isAdding) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setIsAdding(false)}>
        <div className="bg-[#1a1b1d] border border-white/[0.1] rounded-lg p-4 w-[400px] shadow-xl" onClick={(e) => e.stopPropagation()}>
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Goal title..."
              autoFocus
              className="bg-white/[0.05] text-white px-3 py-2 rounded border border-white/[0.1] focus:border-primary focus:outline-none text-sm placeholder:text-white/40"
            />

            {/* Priority Pills */}
            <div>
              <label className="text-xs font-semibold text-white/70 uppercase tracking-wider mb-2 block">Priority (click to select multiple)</label>
              <div className="flex flex-wrap gap-2">
                {priorityOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => togglePriority(option.value)}
                    className={`px-3 py-1.5 rounded text-xs font-medium border transition-all ${option.color} ${
                      priorities.includes(option.value) ? 'ring-2 ring-white/30' : 'opacity-60'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Why does this solve one of the 3 problems?"
              rows={3}
              className="bg-white/[0.05] text-white px-3 py-2 rounded border border-white/[0.1] focus:border-primary focus:outline-none resize-none text-sm placeholder:text-white/40"
            />

            <div className="flex gap-2">
              <button type="submit" className="bg-primary text-black px-4 py-2 rounded hover:opacity-90 transition-opacity text-sm font-semibold flex-1">
                Add Goal
              </button>
              <button type="button" onClick={() => setIsAdding(false)} className="bg-white/[0.05] border border-white/[0.1] text-white px-4 py-2 rounded hover:bg-white/[0.08] transition-colors text-sm font-medium">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  return (
    <button onClick={() => setIsAdding(true)} className="bg-primary text-black px-3 py-1.5 rounded hover:opacity-90 transition-opacity flex items-center gap-2 text-sm font-semibold">
      <Plus className="w-4 h-4" />
      Add Goal
    </button>
  )
}
